import { Injectable, inject } from '@angular/core';
import { BrandingService } from '@next/core-lib/branding';
import { LocaleService } from '@next/core-lib/i18n';

/**
 * Tenant-aware translation service that wraps LocaleService
 * and applies tenant-specific translations and text replacements.
 *
 * For Agravis tenant, replaces Agrifirm branding with Agravis branding.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantAwareTranslationService {
  private readonly localeService = inject(LocaleService);
  private readonly brandingService = inject(BrandingService);

  /**
   * Mapping of translation keys to their tenant-specific variants.
   * Used to redirect to different keys based on the current tenant.
   */
  private readonly tenantKeyMappings: Record<string, Record<string, string>> = {
    Agravis: {
      'final-setup.print-footer': 'final-setup.print-footer-agravis',
      'final-setup.print-company-name': 'final-setup.print-company-name-agravis',
      'final-setup.print-company-email': 'final-setup.print-company-email-agravis',
      'final-setup.print-company-phone': 'final-setup.print-company-phone-agravis',
      'farmer-insights.settings.selected-period-label':
        'farmer-insights.settings.selected-period-label-agravis',
      'farmer-insights.settings.last-weeks-label':
        'farmer-insights.settings.last-weeks-label-agravis',
    },
  };

  /**
   * Translate a key with tenant-aware support.
   * If the current tenant has a mapping for this key, it will use the mapped key instead.
   * Additionally, applies any text replacements for the tenant.
   *
   * @param key - Translation key
   * @param params - Optional parameters for interpolation
   * @returns Translated and tenant-adjusted text
   */
  translate(key: string, params?: { [key: string]: string | Record<string, unknown> }): string {
    const tenant = this.brandingService.isTenant('Agravis') ? 'Agravis' : 'Agrifirm';
    const mappedKey = this.getKeyForTenant(key, tenant);

    let translated = this.localeService.translate(mappedKey, params);
    translated = this.ReplaceTenantPlaceholders(translated, tenant);

    return translated;
  }

  /**
   * Get the translated key, falling back to the original key if mapped key doesn't exist.
   * This ensures backward compatibility if a mapped key hasn't been added to translation files yet.
   *
   * @param key - Original translation key
   * @param tenant - Current tenant name
   * @returns The key to use for translation
   */
  private getKeyForTenant(key: string, tenant: string): string {
    const mappings = this.tenantKeyMappings[tenant];
    if (!mappings?.[key]) {
      return key;
    }

    // Verify the mapped key exists in translations by checking if it translates
    const mappedKey = mappings[key];
    const fallbackKey = key;

    // Try the mapped key first, fall back to original if it doesn't exist
    try {
      const mapped = this.localeService.translate(mappedKey);
      // If the translation is the same as the key itself, assume key doesn't exist
      if (mapped !== mappedKey) {
        return mappedKey;
      }
    } catch {
      // If translation fails, use fallback
    }

    return fallbackKey;
  }

  /**
   * Replaces tenant placeholders and default Agrifirm branding in a text string.
   * Handles `<Tenant>` / `<tenant>` placeholders as well as literal "Agrifirm" / "agrifirm" occurrences.
   *
   * @param text - The text containing placeholders or default brand names
   * @param tenant - The tenant key used to look up the translated tenant name
   * @returns Text with all tenant references replaced
   */
  public readonly ReplaceTenantPlaceholders = (text: string, tenant: string): string => {
    const tenantCapitalized = tenant.charAt(0).toUpperCase() + tenant.slice(1);
    const tenantLower = tenant.charAt(0).toLowerCase() + tenant.slice(1);
    text = text.replaceAll('<Tenant>', tenantCapitalized);
    text = text.replaceAll('<tenant>', tenantLower);
    text = text.replaceAll('Agrifirm', tenantCapitalized);
    text = text.replaceAll('agrifirm', tenantLower);
    return text;
  };
}
